// src/services/firmAuthService.js - Company email verification system
import firebase, { auth, db } from '../firebaseConfig.js';
import { showGeneralMessage } from '../ui/uiUtils.js';

// VERIFICATION METHOD CONFIGURATION
// Switch between 'link' and 'code' based verification
const VERIFICATION_METHOD = 'code'; // Change to 'link' to use link-based verification

// Known company domains that should be allowed for firm access
const FIRM_DOMAINS = [
  'bain.com',
  'bcg.com', 
  'mckinsey.com',
  'accenture.com',
  'deloitte.com',
  'ey.com',
  'kpmg.com',
  'pwc.com'
];

// Personal email domains that should be rejected
const PERSONAL_DOMAINS = [
  'gmail.com', 
  'yahoo.com', 
  'hotmail.com', 
  'outlook.com', 
  'live.com', 
  'icloud.com',
  'protonmail.com',
  'aol.com'
];

/**
 * Validates if an email address is properly formatted
 */
export function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Checks if an email domain is a personal email provider
 */
export function isPersonalEmail(email) {
  if (!isValidEmail(email)) return false;
  const domain = email.toLowerCase().trim().split('@')[1];
  return PERSONAL_DOMAINS.includes(domain);
}

/**
 * Checks if an email domain is a recognized firm domain
 */
export function isFirmEmail(email) {
  if (!isValidEmail(email)) return false;
  const domain = email.toLowerCase().trim().split('@')[1];
  return FIRM_DOMAINS.includes(domain);
}

/**
 * Gets the company domain from an email address
 */
export function getCompanyDomain(email) {
  if (!isValidEmail(email)) return null;
  return email.toLowerCase().trim().split('@')[1];
}

/**
 * Generates a verification token for company email verification
 */
function generateVerificationToken() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

/**
 * Generates a 6-digit verification code for code-based verification
 */
function generateVerificationCode() {
  return Math.floor(100000 + Math.random() * 900000).toString(); // 6-digit code
}

/**
 * Sends company email verification with click-to-copy code
 * @param {string} companyEmail - The company email to send verification to
 * @param {string} verificationCode - The 6-digit verification code
 * @param {string} userEmail - The user's personal email for reference
 */
async function sendCompanyEmailCodeVerification(companyEmail, verificationCode, userEmail) {
  try {
    console.log('📧 Sending code-based verification to:', companyEmail);
    
    // Create professional click-to-copy email
    const emailDoc = {
      to: companyEmail,
      from: 'shane@deckops.ai',
      replyTo: 'shane@deckops.ai',
      message: {
        headers: {
          'X-Mailer': 'deckops.ai Verification System',
          'Return-Path': 'shane@deckops.ai',
          'Sender': 'shane@deckops.ai'
        },
        subject: 'Your deckops.ai Verification Code',
        html: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Verification Code - deckops.ai</title>
    <style>
        body { font-family: monospace; background-color: #000; color: #fff; margin: 0; padding: 20px; }
        .container { max-width: 500px; margin: 0 auto; border: 2px solid #00FF00; background-color: #111; }
        .header { padding: 30px; text-align: center; border-bottom: 2px solid #00FF00; }
        .logo { font-size: 28px; font-weight: bold; color: #00FF00; margin-bottom: 10px; }
        .tagline { font-size: 12px; color: #ccc; text-transform: uppercase; }
        .content { padding: 30px; text-align: center; }
        .title { font-size: 18px; color: #00FF00; font-weight: bold; margin-bottom: 25px; }
        .code-container { background-color: #222; border: 2px solid #00FF00; padding: 25px; margin: 20px 0; position: relative; cursor: pointer; }
        .code-container:hover { background-color: #333; border-color: #00DD00; }
        .code { font-size: 36px; font-weight: bold; color: #00FF00; letter-spacing: 8px; margin-bottom: 15px; user-select: all; }
        .copy-instruction { font-size: 14px; color: #ccc; margin-bottom: 10px; }
        .copy-button { display: inline-block; background-color: #00FF00; color: #000; padding: 8px 16px; font-size: 12px; font-weight: bold; border: none; cursor: pointer; margin-top: 10px; }
        .copy-button:hover { background-color: #00DD00; }
        .instructions { background-color: #222; padding: 20px; margin: 20px 0; border-left: 4px solid #00FF00; }
        .instructions h3 { color: #00FF00; margin-top: 0; font-size: 14px; }
        .instructions p { font-size: 12px; line-height: 1.5; margin: 8px 0; }
        .footer { background-color: #000; padding: 20px; text-align: center; border-top: 2px solid #00FF00; font-size: 11px; color: #888; }
        .contact { color: #00FF00; text-decoration: none; }
    </style>
    <script>
        function copyCode() {
            const code = '${verificationCode}';
            if (navigator.clipboard) {
                navigator.clipboard.writeText(code).then(function() {
                    showCopyFeedback();
                });
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = code;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                showCopyFeedback();
            }
        }
        
        function showCopyFeedback() {
            const button = document.getElementById('copyButton');
            const original = button.innerHTML;
            button.innerHTML = '✓ COPIED!';
            button.style.backgroundColor = '#00DD00';
            setTimeout(() => {
                button.innerHTML = original;
                button.style.backgroundColor = '#00FF00';
            }, 2000);
        }
    </script>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">deckops.ai</div>
            <div class="tagline">Elite Productivity Training Platform</div>
        </div>
        
        <div class="content">
            <div class="title">🔐 SECURE ACCESS VERIFICATION</div>
            
            <div class="code-container" onclick="copyCode()">
                <div class="copy-instruction">Your verification code:</div>
                <div class="code" id="verificationCode">${verificationCode}</div>
                <button class="copy-button" id="copyButton">📋 CLICK TO COPY</button>
            </div>
            
            <div class="instructions">
                <h3>📱 HOW TO USE:</h3>
                <p>1. Click the code above to copy it</p>
                <p>2. Return to deckops.ai in your browser</p>
                <p>3. Paste the code in the verification field</p>
                <p>4. Enjoy your firm-specific training access!</p>
            </div>
            
            <div style="background-color: #222; border: 1px solid #ff4444; padding: 15px; margin: 20px 0;">
                <div style="color: #ff4444; font-weight: bold; margin-bottom: 10px;">⚠️ SECURITY NOTICE</div>
                <div style="font-size: 12px; line-height: 1.4;">
                    • Code expires in 15 minutes<br>
                    • One company email per account<br>
                    • Grants access to firm training modules<br>
                    • Keep this code confidential
                </div>
            </div>
        </div>
        
        <div class="footer">
            Sent from deckops.ai secure verification system<br>
            Questions? <a href="mailto:shane@deckops.ai" class="contact">shane@deckops.ai</a><br>
            <strong style="color: #00FF00;">deckops.ai</strong> - Elite Productivity Training
        </div>
    </div>
</body>
</html>`,
        text: `DECKOPS.AI - VERIFICATION CODE

🔐 SECURE ACCESS VERIFICATION

Your verification code: ${verificationCode}

HOW TO USE:
1. Copy this code: ${verificationCode}
2. Return to deckops.ai in your browser
3. Paste the code in the verification field
4. Enjoy your firm-specific training access!

SECURITY NOTICE:
▶ Code expires in 15 minutes
▶ One company email per account
▶ Grants access to firm training modules  
▶ Keep this code confidential

Questions? Contact us at shane@deckops.ai

deckops.ai - Elite Productivity Training Platform`
      }
    };

    console.log('📧 Code verification email document:', JSON.stringify(emailDoc, null, 2));

    // Write to mail collection - Firebase Email Extension will process this
    await db.collection("mail").add(emailDoc);
    console.log(`✅ Code verification email queued for ${companyEmail}`);

  } catch (error) {
    console.error("Error sending code verification email:", error);
    throw new Error("Failed to send verification email");
  }
}

/**
 * Sends company email verification via Firebase Email Extension (ORIGINAL LINK-BASED)
 * @param {string} companyEmail - The company email to send verification to
 * @param {string} verificationToken - The verification token to include in email
 * @param {string} userEmail - The user's personal email for reference
 */
async function sendCompanyEmailLinkVerification(companyEmail, verificationToken, userEmail) {
  try {
    // DEV ENVIRONMENT DOMAIN for verification links (Vercel deployment)
    const DEV_DOMAIN = 'https://88deckops88-git-feature-shortcut-flow-fixes-shanejhs-projects.vercel.app';
    const verificationLink = `${DEV_DOMAIN}/verify-company-email.html?token=${verificationToken}`;
    
    console.log('📧 Generated verification link:', verificationLink);
    
    // Create email document for Firebase Email Extension (CORRECT FORMAT)
    const emailDoc = {
      to: companyEmail,
      from: 'shane@deckops.ai',
      replyTo: 'shane@deckops.ai',
      message: {
        headers: {
          'X-Mailer': 'deckops.ai Verification System',
          'Return-Path': 'shane@deckops.ai',
          'Sender': 'shane@deckops.ai'
        },
        subject: 'Verify Your Company Email - deckops.ai',
        html: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Company Email Verification - deckops.ai</title>
    <style>
        body { font-family: monospace; background-color: #000; color: #fff; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; border: 2px solid #00FF00; }
        .header { background-color: #111; padding: 30px; text-align: center; border-bottom: 2px solid #00FF00; }
        .logo { font-size: 36px; font-weight: bold; color: #00FF00; margin-bottom: 10px; }
        .tagline { font-size: 12px; color: #ccc; text-transform: uppercase; }
        .content { padding: 30px; }
        .greeting { font-size: 18px; color: #00FF00; font-weight: bold; margin-bottom: 20px; }
        .message { font-size: 14px; line-height: 1.6; margin-bottom: 30px; }
        .verify-section { text-align: center; margin: 30px 0; padding: 25px; background-color: #111; border: 1px solid #00FF00; }
        .verify-button { display: inline-block; background-color: #00FF00; color: #000; padding: 15px 30px; text-decoration: none; font-weight: bold; font-size: 16px; border: 2px solid #00FF00; }
        .link-section { margin-top: 20px; padding: 15px; background-color: #222; }
        .link-text { font-size: 11px; color: #ccc; word-break: break-all; }
        .footer { background-color: #111; padding: 20px; text-align: center; border-top: 2px solid #00FF00; font-size: 11px; color: #888; }
        .contact { color: #00FF00; text-decoration: none; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">deckops.ai</div>
            <div class="tagline">Elite Productivity Training Platform</div>
        </div>
        
        <div class="content">
            <div class="greeting">SECURE ACCESS VERIFICATION</div>
            
            <div class="message">
                A request has been made to associate your company email <strong style="color: #00FF00;">${companyEmail}</strong> with the deckops.ai training platform.
                <br><br>
                This will grant you access to firm-specific productivity training modules.
            </div>
            
            <div class="verify-section">
                <div style="margin-bottom: 15px; color: #ccc; font-size: 12px;">CLICK TO VERIFY YOUR ACCESS</div>
                <a href="${verificationLink}" class="verify-button">🔐 VERIFY COMPANY EMAIL</a>
                
                <div class="link-section">
                    <div style="color: #ccc; font-size: 11px; margin-bottom: 10px;">Or copy this link:</div>
                    <div class="link-text">${verificationLink}</div>
                </div>
            </div>
            
            <div style="background-color: #222; border: 1px solid #ff4444; padding: 15px; margin: 20px 0;">
                <div style="color: #ff4444; font-weight: bold; margin-bottom: 10px;">⚠️ SECURITY NOTICE</div>
                <div style="font-size: 12px; line-height: 1.4;">
                    • Link expires in 24 hours<br>
                    • One company email per account<br>
                    • Grants access to firm training modules<br>
                    • Ignore if you didn't request this
                </div>
            </div>
        </div>
        
        <div class="footer">
            Sent from deckops.ai secure verification system<br>
            Questions? <a href="mailto:shane@deckops.ai" class="contact">shane@deckops.ai</a><br>
            <strong style="color: #00FF00;">deckops.ai</strong> - Elite Productivity Training
        </div>
    </div>
</body>
</html>`,
        text: `DECKOPS.AI - COMPANY EMAIL VERIFICATION

SECURE ACCESS VERIFICATION

A request has been made to associate your company email address (${companyEmail}) with the deckops.ai training platform.

This will grant you access to firm-specific productivity training modules and advanced analytics.

VERIFY YOUR ACCESS:
${verificationLink}

SECURITY NOTICE:
▶ This verification link expires in 24 hours
▶ Only one company email per user account is permitted  
▶ Verification grants access to firm-specific training modules
▶ If you didn't request this, please ignore this email

Questions? Contact us at shane@deckops.ai

deckops.ai - Elite Productivity Training Platform`
      }
    };

    console.log('📧 Email document being sent:', JSON.stringify(emailDoc, null, 2));

    // Write to mail collection - Firebase Email Extension will process this
    await db.collection("mail").add(emailDoc);
    console.log(`✅ Company verification email queued for ${companyEmail}`);

  } catch (error) {
    console.error("Error sending company email verification:", error);
    throw new Error("Failed to send verification email");
  }
}

/**
 * Initiates company email verification process
 * @param {string} companyEmail - The company email to verify
 * @returns {Promise<{success: boolean, message: string}>}
 */
/**
 * Main company email verification function - routes to link or code based on config
 * @param {string} companyEmail - The company email to verify
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function initiateCompanyEmailVerification(companyEmail) {
  console.log(`🔧 Using verification method: ${VERIFICATION_METHOD}`);
  
  if (VERIFICATION_METHOD === 'code') {
    return await initiateCodeBasedVerification(companyEmail);
  } else {
    return await initiateLinkBasedVerification(companyEmail);
  }
}

/**
 * Code-based verification initiation
 * @param {string} companyEmail - The company email to verify
 * @returns {Promise<{success: boolean, message: string}>}
 */
async function initiateCodeBasedVerification(companyEmail) {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, message: "User must be logged in to verify company email." };
    }

    // Validate email format
    if (!isValidEmail(companyEmail)) {
      return { success: false, message: "Please enter a valid email address." };
    }

    // Check if it's a personal email
    if (isPersonalEmail(companyEmail)) {
      return { success: false, message: "Please use a company email, not a personal email address." };
    }

    // Normalize email
    const normalizedEmail = companyEmail.toLowerCase().trim();
    
    // Check if this company email is already verified by another user
    console.log('🔍 Checking existing verification for:', normalizedEmail);
    const existingVerification = await db.collection("companyEmailVerifications")
      .where("companyEmail", "==", normalizedEmail)
      .where("verified", "==", true)
      .get();
    console.log('✅ Existing verification check completed');
    
    if (!existingVerification.empty) {
      return { success: false, message: "This company email is already verified by another user." };
    }

    // Generate 6-digit verification code
    const verificationCode = generateVerificationCode();
    const expiresAt = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes from now

    // Store verification request in Firestore
    const verificationData = {
      userId: user.uid,
      userEmail: user.email,
      companyEmail: normalizedEmail,
      companyDomain: getCompanyDomain(normalizedEmail),
      verificationCode: verificationCode,
      verificationMethod: 'code',
      verified: false,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      expiresAt: expiresAt
    };

    console.log('🔍 Writing to companyEmailVerifications collection...');
    await db.collection("companyEmailVerifications").doc(user.uid).set(verificationData);
    console.log('✅ CompanyEmailVerifications write completed');

    // Update user profile with pending company email
    console.log('🔍 Updating userProfiles collection...');
    await db.collection("userProfiles").doc(user.uid).update({
      companyEmail: normalizedEmail,
      companyEmailVerified: false,
      companyEmailVerificationRequested: firebase.firestore.FieldValue.serverTimestamp()
    });
    console.log('✅ UserProfiles update completed');

    console.log(`Code-based verification initiated for ${user.email} -> ${normalizedEmail}`);
    
    // Send verification code email
    await sendCompanyEmailCodeVerification(normalizedEmail, verificationCode, user.email);
    
    return { 
      success: true, 
      message: "Verification code sent! Please check your company email and enter the 6-digit code below." 
    };

  } catch (error) {
    console.error("Error initiating code-based verification:", error);
    return { success: false, message: "Failed to initiate verification. Please try again." };
  }
}

/**
 * Link-based verification initiation (ORIGINAL SYSTEM)
 * @param {string} companyEmail - The company email to verify
 * @returns {Promise<{success: boolean, message: string}>}
 */
async function initiateLinkBasedVerification(companyEmail) {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, message: "User must be logged in to verify company email." };
    }

    // Validate email format
    if (!isValidEmail(companyEmail)) {
      return { success: false, message: "Please enter a valid email address." };
    }

    // Check if it's a personal email
    if (isPersonalEmail(companyEmail)) {
      return { success: false, message: "Please use a company email, not a personal email address." };
    }

    // Normalize email
    const normalizedEmail = companyEmail.toLowerCase().trim();
    
    // Check if this company email is already verified by another user
    console.log('🔍 Checking existing verification for:', normalizedEmail);
    const existingVerification = await db.collection("companyEmailVerifications")
      .where("companyEmail", "==", normalizedEmail)
      .where("verified", "==", true)
      .get();
    console.log('✅ Existing verification check completed');
    
    if (!existingVerification.empty) {
      return { success: false, message: "This company email is already verified by another user." };
    }

    // Generate verification token
    const verificationToken = generateVerificationToken();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now

    // Store verification request in Firestore
    const verificationData = {
      userId: user.uid,
      userEmail: user.email,
      companyEmail: normalizedEmail,
      companyDomain: getCompanyDomain(normalizedEmail),
      verificationToken: verificationToken,
      verificationMethod: 'link',
      verified: false,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      expiresAt: expiresAt
    };

    console.log('🔍 Writing to companyEmailVerifications collection...');
    await db.collection("companyEmailVerifications").doc(user.uid).set(verificationData);
    console.log('✅ CompanyEmailVerifications write completed');

    // Update user profile with pending company email
    console.log('🔍 Updating userProfiles collection...');
    await db.collection("userProfiles").doc(user.uid).update({
      companyEmail: normalizedEmail,
      companyEmailVerified: false,
      companyEmailVerificationRequested: firebase.firestore.FieldValue.serverTimestamp()
    });
    console.log('✅ UserProfiles update completed');

    console.log(`Link-based verification initiated for ${user.email} -> ${normalizedEmail}`);
    
    // Send verification email using Firebase Email Extension
    await sendCompanyEmailLinkVerification(normalizedEmail, verificationToken, user.email);
    
    return { 
      success: true, 
      message: "Company email verification sent! Please check your company email inbox and click the verification link." 
    };

  } catch (error) {
    console.error("Error initiating link-based verification:", error);
    return { success: false, message: "Failed to initiate verification. Please try again." };
  }
}

/**
 * Verifies a company email using a 6-digit verification code
 * @param {string} code - The 6-digit verification code
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function verifyCompanyEmailCode(code) {
  try {
    if (!code || code.length !== 6) {
      return { success: false, message: "Please enter a valid 6-digit verification code." };
    }

    const user = auth.currentUser;
    if (!user) {
      return { success: false, message: "User must be logged in to verify." };
    }

    // Find verification record by code and user
    const verificationQuery = await db.collection("companyEmailVerifications")
      .where("verificationCode", "==", code)
      .where("userId", "==", user.uid)
      .where("verified", "==", false)
      .get();

    if (verificationQuery.empty) {
      return { success: false, message: "Invalid verification code. Please check and try again." };
    }

    const verificationDoc = verificationQuery.docs[0];
    const verificationData = verificationDoc.data();

    // Check if code has expired
    const now = new Date();
    const expiresAt = verificationData.expiresAt.toDate();
    if (now > expiresAt) {
      return { success: false, message: "Verification code has expired. Please request a new code." };
    }

    // Mark verification as complete
    await db.collection("companyEmailVerifications").doc(verificationDoc.id).update({
      verified: true,
      verifiedAt: firebase.firestore.FieldValue.serverTimestamp()
    });

    // Update user profile
    await db.collection("userProfiles").doc(verificationData.userId).update({
      companyEmailVerified: true,
      companyEmailVerifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
      firmAccess: true,
      firmDomain: verificationData.companyDomain
    });

    console.log(`Company email verified successfully for user ${verificationData.userId}`);
    
    return { success: true, message: "Company email verified successfully! You now have access to firm-specific features." };

  } catch (error) {
    console.error("Error verifying company email code:", error);
    return { success: false, message: "Failed to verify code. Please try again." };
  }
}

/**
 * Verifies a company email using the verification token (ORIGINAL LINK-BASED)
 * @param {string} token - The verification token from the email
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function verifyCompanyEmailToken(token) {
  try {
    if (!token) {
      return { success: false, message: "Verification token is required." };
    }

    // Find verification record by token
    const verificationQuery = await db.collection("companyEmailVerifications")
      .where("verificationToken", "==", token)
      .where("verified", "==", false)
      .get();

    if (verificationQuery.empty) {
      return { success: false, message: "Invalid or expired verification token." };
    }

    const verificationDoc = verificationQuery.docs[0];
    const verificationData = verificationDoc.data();

    // Check if token has expired
    const now = new Date();
    const expiresAt = verificationData.expiresAt.toDate();
    if (now > expiresAt) {
      return { success: false, message: "Verification token has expired. Please request a new verification." };
    }

    // Mark verification as complete
    await db.collection("companyEmailVerifications").doc(verificationDoc.id).update({
      verified: true,
      verifiedAt: firebase.firestore.FieldValue.serverTimestamp()
    });

    // Update user profile
    await db.collection("userProfiles").doc(verificationData.userId).update({
      companyEmailVerified: true,
      companyEmailVerifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
      firmAccess: true,
      firmDomain: verificationData.companyDomain
    });

    console.log(`Company email verified successfully for user ${verificationData.userId}`);
    
    return { success: true, message: "Company email verified successfully! You now have access to firm-specific features." };

  } catch (error) {
    console.error("Error verifying company email token:", error);
    return { success: false, message: "Failed to verify token. Please try again." };
  }
}

/**
 * Gets the company email verification status for a user
 * @param {string} userId - The user ID to check
 * @returns {Promise<{companyEmail: string|null, verified: boolean, domain: string|null}>}
 */
export async function getCompanyEmailStatus(userId) {
  try {
    const userProfile = await db.collection("userProfiles").doc(userId).get();
    
    if (!userProfile.exists) {
      return { companyEmail: null, verified: false, domain: null };
    }

    const data = userProfile.data();
    return {
      companyEmail: data.companyEmail || null,
      verified: data.companyEmailVerified || false,
      domain: data.firmDomain || null
    };
  } catch (error) {
    console.error("Error getting company email status:", error);
    return { companyEmail: null, verified: false, domain: null };
  }
}

/**
 * Checks if a user has verified firm access
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>}
 */
export async function checkFirmAccess(userId) {
  try {
    const status = await getCompanyEmailStatus(userId);
    return status.verified;
  } catch (error) {
    console.error("Error checking firm access:", error);
    return false;
  }
}

/**
 * Revokes company email verification (admin function)
 * @param {string} userId - The user ID to revoke access for
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function revokeCompanyEmailVerification(userId) {
  try {
    // Update user profile
    await db.collection("userProfiles").doc(userId).update({
      companyEmailVerified: false,
      firmAccess: false,
      companyEmailRevokedAt: firebase.firestore.FieldValue.serverTimestamp()
    });

    // Update verification record
    const verificationQuery = await db.collection("companyEmailVerifications")
      .where("userId", "==", userId)
      .get();

    if (!verificationQuery.empty) {
      const batch = db.batch();
      verificationQuery.docs.forEach(doc => {
        batch.update(doc.ref, {
          verified: false,
          revokedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      });
      await batch.commit();
    }

    console.log(`Company email verification revoked for user ${userId}`);
    return { success: true, message: "Company email verification revoked successfully." };

  } catch (error) {
    console.error("Error revoking company email verification:", error);
    return { success: false, message: "Failed to revoke verification. Please try again." };
  }
}



// Export all functions
export default {
  isValidEmail,
  isPersonalEmail,
  isFirmEmail,
  getCompanyDomain,
  initiateCompanyEmailVerification,
  verifyCompanyEmailToken,
  getCompanyEmailStatus,
  checkFirmAccess,
  revokeCompanyEmailVerification
};
